import { useEffect, useRef } from "react";
import { Button, Select, Stack, Typography } from "../../design-system";
import { pendingShellCopy } from "../../routing/reload-failure-copy";
import { exposureContent } from "./exposure-content";
import { findOnFileScreen, onFileItemHeading } from "./on-file-render";
import type { OnFileRenderContext } from "./on-file-render";
import type { OnFilePickerSpec } from "./on-file-fields";
import type { OnFileScreenItem } from "../../state/on-file-port";

export interface OnFilePickerProps {
  readonly id: string;
  readonly label: string;
  readonly pickerSpec: OnFilePickerSpec;
  /** H2 `emptyLabel`: the placeholder while nothing is picked. */
  readonly emptyLabel: string;
  /** H2 `noneLeftLabel`: the placeholder once nothing is left to pick. */
  readonly noneLeftLabel: string;
  /** A fixed choice offered after the records (H2's "Add a new commodity"). */
  readonly newOption?: { readonly value: string; readonly label: string };
  /** Fired when `newOption` is chosen. */
  readonly onPickNew?: () => void;
  readonly onFile: OnFileRenderContext | undefined;
  /** The current field value — `""` means nothing picked yet (AC-2:
   * "prefill fields are hidden until a real on-file item is picked"). */
  readonly value: string;
  /** `epicRecordId`s already picked by a SIBLING repeat item for the SAME
   * picker field (AC-2: "withholds one already picked in another
   * block"). */
  readonly excluded: ReadonlySet<number>;
  readonly error?: string;
  readonly disabled: boolean;
  /** Fired with the FULL on-file item once a real pick is made — the
   * caller both records `{screenKey, epicRecordId}` and prefills this
   * picker's declared fields from `item.attributes` (AC-2/AC-3). */
  readonly onPick: (item: OnFileScreenItem) => void;
  /** Shared, section-instance-wide claim (`useFailureFocusClaim`) so
   * exactly one retry control focuses itself when SEVERAL questions in
   * the same section fail at once — see that function's own doc comment. */
  readonly claimFailureFocus: () => boolean;
}

/**
 * The "Which X?" picker (TG-5, AC-2/AC-3/AC-4): a `Select` over the section instance's own
 * on-file records. H2: the placeholder names what to pick, and once nothing is left it says so
 * ("No vehicles left to select"). Always the sole owner of the failed-read retry control and
 * focus for its question (SC 2.4.3): unlike `OnFilePanel`, a picker is never behind a collapsed
 * disclosure.
 */
export function OnFilePicker({
  id,
  label,
  pickerSpec,
  emptyLabel,
  noneLeftLabel,
  newOption,
  onPickNew,
  onFile,
  value,
  excluded,
  error,
  disabled,
  onPick,
  claimFailureFocus,
}: Readonly<OnFilePickerProps>) {
  const retryWrapperRef = useRef<HTMLSpanElement | null>(null);
  const status = onFile?.state.status ?? "loading";
  const failed = status === "failed";
  useEffect(() => {
    if (failed && claimFailureFocus()) {
      retryWrapperRef.current?.querySelector("button")?.focus();
    }
  }, [failed, claimFailureFocus]);

  if (failed) {
    return (
      <Stack gap="sm">
        <Typography variant="body">{exposureContent.onFileReadFailedMessage}</Typography>
        <span ref={retryWrapperRef}>
          <Button emphasis="secondary" onClick={() => onFile?.onRetry()} testId={`${id}-retry`}>
            {exposureContent.onFileRetryLabel}
          </Button>
        </span>
      </Stack>
    );
  }

  if (status === "loading") {
    return (
      <Select
        id={id}
        label={label}
        value=""
        onChange={() => {}}
        options={[]}
        emptyLabel={pendingShellCopy.loadingLabel}
        disabled
      />
    );
  }

  const screen = findOnFileScreen(onFile, pickerSpec.screenKey);
  const availableItems = (screen?.items ?? []).filter(
    (item) => item.epicRecordId !== null && !excluded.has(item.epicRecordId),
  );
  const options = [
    ...availableItems.map((item) => ({
      value: String(item.epicRecordId),
      label:
        onFileItemHeading(item, pickerSpec.labelAttributes) || exposureContent.onFileItemFallbackLabel(item.rowIndex + 1),
    })),
    ...(newOption !== undefined ? [newOption] : []),
  ];

  return (
    <Select
      id={id}
      label={label}
      value={options.some((option) => option.value === value) ? value : ""}
      onChange={(nextValue) => {
        if (newOption !== undefined && nextValue === newOption.value) {
          onPickNew?.();
          return;
        }
        const picked = availableItems.find((item) => String(item.epicRecordId) === nextValue);
        if (picked !== undefined) {
          onPick(picked);
        }
      }}
      options={options}
      emptyLabel={options.length > 0 ? emptyLabel : noneLeftLabel}
      error={error !== undefined}
      disabled={disabled}
      {...(error !== undefined ? { errorText: error } : {})}
    />
  );
}
